import {
  Badge,
  Button,
  Checkbox,
  CloseButton,
  Divider,
  Group,
  Select,
  Stack,
  Text,
  Textarea,
  Title,
} from "@mantine/core";
import { IconLock, IconPencil, IconTrash } from "@tabler/icons-react";
import { useState } from "react";
import { usePermission } from "@how/app-kit";
import {
  ActivityTimeline,
  ConfirmActionModal,
  ErrorState,
  LoadingState,
  RecordDrawer,
  formatDateOnly,
  type ActivityItem,
} from "@how/ui";
import { semanticColor } from "@how/design-system";
import {
  ContactsSection,
  CustomFieldsSection,
  PersonDetailsForm,
  PreferencesSection,
} from "./PersonSections";
import { PortalLoginSection } from "./PortalLoginSection";
import { PathwaySection } from "../membership/PathwaySection";
import { ComingUpSection } from "../events/ComingUpSection";
import { CollectorsSection } from "./CollectorsSection";
import { isMinor } from "./minor";
import { useAddNote, useDeletePerson, usePerson } from "./queries";
import { useTagPerson, useTags, useUntagPerson } from "./tag-queries";
import { useCampuses } from "../campuses/campus-queries";

export interface PersonDrawerProps {
  readonly personId: string | null;
  readonly onClose: () => void;
}

const dateTimeText = (value: string): string => new Date(value).toLocaleString();

/** Maps a timeline entry's kind onto a semantic role, so a deletion reads differently from a creation. */
function timelineRole(kind: string): NonNullable<ActivityItem["role"]> {
  if (kind.endsWith(".deleted") || kind.endsWith(".removed")) return "danger";
  if (kind === "person.created") return "success";
  return "neutral";
}

/**
 * One person's record, opened from the list without leaving it. Everything here is read through the same policies as
 * the list, so a section that is empty may be empty or may be gated, and the drawer does not pretend to know which.
 */
export function PersonDrawer({ personId, onClose }: PersonDrawerProps) {
  const record = usePerson(personId);
  const canWriteNotes = usePermission("notes.write", { write: true });
  const canWritePeople = usePermission("people.write", { write: true });
  const canManagePreferences = usePermission("preferences.manage", { write: true });
  const deletePerson = useDeletePerson();
  const [editing, setEditing] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const canTag = usePermission("tags.manage", { write: true });
  const allTags = useTags();
  const campuses = useCampuses();
  const tagPerson = useTagPerson();
  const untag = useUntagPerson();
  const addNote = useAddNote();
  const [note, setNote] = useState("");
  const [confidential, setConfidential] = useState(false);

  const person = record.data?.person;
  const fullName =
    person === undefined ? "Person" : `${person.first_name} ${person.last_name}`.trim();

  return (
    <RecordDrawer
      opened={personId !== null}
      onClose={() => {
        setEditing(false);
        onClose();
      }}
      title={fullName}
      subtitle={
        person === undefined ? undefined : (
          <Group justify="space-between" wrap="nowrap">
            <Text size="sm" c="dimmed">
              {person.preferred_name === null ? "" : `Goes by ${person.preferred_name}. `}
              {person.birthdate === null
                ? "No birthdate recorded"
                : `Born ${formatDateOnly(person.birthdate)}`}
              {person.primary_campus_id === null
                ? ""
                : `. ${campuses.data?.find((c) => c.id === person.primary_campus_id)?.name ?? "Home campus set"}`}
            </Text>
            {canWritePeople.data === true && !editing ? (
              <Button
                variant="subtle"
                size="xs"
                // Several "Edit" buttons share this drawer, so each carries a name that says what it edits.
                aria-label="Edit name and birthdate"
                leftSection={<IconPencil size={14} />}
                onClick={() => {
                  setEditing(true);
                }}
              >
                Edit
              </Button>
            ) : null}
          </Group>
        )
      }
    >
      {record.isPending ? (
        <LoadingState rows={4} />
      ) : record.isError ? (
        <ErrorState
          message="Could not load this person."
          onRetry={() => {
            void record.refetch();
          }}
        />
      ) : editing ? (
        <PersonDetailsForm
          person={record.data.person}
          onDone={() => {
            setEditing(false);
          }}
        />
      ) : (
        <Stack gap="lg" mt="md">
          {personId === null ? null : (
            <ContactsSection
              personId={personId}
              contacts={record.data.contacts}
              canWrite={canWritePeople.data === true}
            />
          )}

          <section>
            <Title order={4}>Household</Title>
            {record.data.households.length === 0 ? (
              <Text size="sm" c="dimmed">
                Not in a household.
              </Text>
            ) : (
              <Stack gap={4} mt="xs">
                {record.data.households.map((household) => (
                  <Group key={household.name} gap="xs">
                    <Text size="sm">{household.name}</Text>
                    <Text size="xs" c="dimmed">
                      {household.role}
                    </Text>
                  </Group>
                ))}
              </Stack>
            )}
          </section>

          {personId === null ? null : <ComingUpSection personId={personId} />}

          {personId !== null &&
          person !== undefined &&
          isMinor(
            person.birthdate,
            record.data.households.map((h) => h.role),
          ) ? (
            <CollectorsSection personId={personId} canWrite={canWritePeople.data === true} />
          ) : null}

          {personId === null ? null : <PathwaySection personId={personId} />}

          {personId === null || person === undefined ? null : (
            <section>
              <PortalLoginSection
                personId={personId}
                personName={fullName}
                authUserId={person.auth_user_id}
              />
            </section>
          )}

          {record.data.tags.length === 0 && canTag.data !== true ? null : (
            <section>
              <Title order={4}>Tags</Title>
              <Group gap="xs" mt="xs">
                {record.data.tags.length === 0 ? (
                  <Text size="sm" c="dimmed">
                    No tags.
                  </Text>
                ) : null}
                {record.data.tags.map((tag) => (
                  // A tag stores a semantic role, not a Mantine colour: handing "success" straight to Mantine
                  // silently fell back to neutral, so every tag rendered grey.
                  <Badge
                    key={tag.id}
                    variant="light"
                    color={semanticColor(tag.color)}
                    {...(canTag.data === true
                      ? {
                          rightSection: (
                            <CloseButton
                              size="xs"
                              aria-label={`Remove the ${tag.name} tag`}
                              onClick={() => {
                                untag.mutate(tag.personTagId);
                              }}
                            />
                          ),
                        }
                      : {})}
                  >
                    {tag.name}
                  </Badge>
                ))}
              </Group>
              {canTag.data === true ? (
                <Select
                  mt="xs"
                  aria-label="Add a tag"
                  placeholder="Add a tag"
                  size="xs"
                  w={220}
                  searchable
                  value={null}
                  data={(allTags.data ?? [])
                    .filter((tag) => !record.data.tags.some((held) => held.id === tag.id))
                    .map((tag) => ({ value: tag.id, label: tag.name }))}
                  nothingFoundMessage="No other tags"
                  onChange={(value) => {
                    if (value !== null && personId !== null)
                      tagPerson.mutate({ personId, tagId: value });
                  }}
                />
              ) : null}
            </section>
          )}

          {personId === null ? null : (
            <>
              <CustomFieldsSection
                personId={personId}
                fields={record.data.fields}
                canWrite={canWritePeople.data === true}
              />
              <PreferencesSection
                personId={personId}
                preferences={record.data.preferences}
                canManage={canManagePreferences.data === true}
              />
            </>
          )}

          <Divider />

          <section>
            <Title order={4}>Notes</Title>
            {record.data.notes.length === 0 ? (
              <Text size="sm" c="dimmed">
                No notes you can see.
              </Text>
            ) : (
              <Stack gap="sm" mt="xs">
                {record.data.notes.map((entry) => (
                  <Stack key={entry.id} gap={2}>
                    <Group gap="xs">
                      <Text size="xs" c="dimmed">
                        {dateTimeText(entry.created_at)}
                      </Text>
                      {entry.is_confidential ? (
                        <Badge
                          size="sm"
                          variant="light"
                          color="violet"
                          leftSection={<IconLock size={11} />}
                        >
                          Confidential
                        </Badge>
                      ) : null}
                    </Group>
                    <Text size="sm">{entry.body}</Text>
                  </Stack>
                ))}
              </Stack>
            )}

            {canWriteNotes.data === true && personId !== null ? (
              <Stack gap="xs" mt="md">
                <Textarea
                  label="Add a note"
                  placeholder="What happened, in a sentence or two."
                  value={note}
                  onChange={(event) => {
                    setNote(event.currentTarget.value);
                  }}
                  autosize
                  minRows={2}
                />
                <Checkbox
                  label="Confidential: only staff with the confidential notes permission can read it"
                  checked={confidential}
                  onChange={(event) => {
                    setConfidential(event.currentTarget.checked);
                  }}
                />
                <Group justify="flex-end">
                  <Button
                    size="sm"
                    disabled={note.trim().length === 0}
                    loading={addNote.isPending}
                    onClick={() => {
                      const body = note.trim();
                      addNote.mutate(
                        { personId, body, confidential },
                        {
                          onSuccess: () => {
                            setNote("");
                            setConfidential(false);
                          },
                        },
                      );
                    }}
                  >
                    Save note
                  </Button>
                </Group>
                {addNote.isError ? (
                  <Text size="sm" c="red">
                    The note was not saved. Check your access and try again.
                  </Text>
                ) : null}
              </Stack>
            ) : null}
          </section>

          <Divider />

          <section>
            <Title order={4}>History</Title>
            <div style={{ marginTop: 12 }}>
              <ActivityTimeline
                emptyText="Nothing recorded yet."
                items={record.data.activities.map((entry) => ({
                  id: String(entry.id),
                  title: entry.summary,
                  when: dateTimeText(entry.occurred_at),
                  role: timelineRole(entry.kind),
                }))}
              />
            </div>
          </section>

          {canWritePeople.data === true ? (
            <>
              <Divider />
              <Group>
                <Button
                  variant="outline"
                  color="red"
                  size="sm"
                  leftSection={<IconTrash size={14} />}
                  onClick={() => {
                    setConfirmDelete(true);
                  }}
                >
                  Delete person
                </Button>
              </Group>
            </>
          ) : null}
        </Stack>
      )}

      <ConfirmActionModal
        opened={confirmDelete}
        onClose={() => {
          setConfirmDelete(false);
        }}
        title="Delete person"
        message={`Delete ${fullName}? They leave the directory, their households and every list. The record is kept and can be restored; nothing is purged.`}
        confirmLabel="Delete person"
        danger
        loading={deletePerson.isPending}
        onConfirm={() => {
          if (personId === null) return;
          deletePerson.mutate(
            { personId },
            {
              onSuccess: () => {
                setConfirmDelete(false);
                setEditing(false);
                onClose();
              },
            },
          );
        }}
      />
    </RecordDrawer>
  );
}
